import ClientCache from "@/utils/login/ClientCache";
import LinkUtils from "@/utils/common/LinkUtils";
import ObjectChecker from "@/utils/common/ObjectChecker";
import CommonFieldIds from "@/utils/constants/CommonFieldIds";
import StringConstants from "@/utils/constants/StringConstants";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import {AG_GRID_LOCALE_EG, AG_GRID_LOCALE_EN} from "@ag-grid-community/locale"
import Language from "@/utils/constants/Language.ts";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import {GridApi} from "ag-grid-community";
import NaMaText = namaTypes.NaMaText;

let mWindow = (window as any);

export default class Translator {
  static readonly LTR = 'ltr';
  static readonly RTL = 'rtl';
  static locale: string = 'ar';
  static localeFromUrl: string = '';
  static DATE_LABELS = Translator.isArabic() ? {today: 'اليوم', yesterday: 'أمس'} : {today: 'Today', yesterday: 'Yesterday'};
  private static dictionary: Record<string, any>;
  private static originalLang: string | null = null;

  public static async reloadTranslations() {
    await Translator.loadScript("translation.js?lang=Nama&ts=" + new Date().getTime());
    await Translator.loadScript("translation.js?lang=Arabic&ts=" + new Date().getTime());
    await Translator.loadScript("translation.js?lang=English&ts=" + new Date().getTime());
    if (!useNamaFlagsStore().showIdsActive) {
      useNamaFlagsStore().showIdsActive = true;
      setTimeout(() => {
        useNamaFlagsStore().showIdsActive = false;
      })
    }
  }

  static loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.type = 'text/javascript';

      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
      document.head.appendChild(script);
    });
  }
  static translateId(id: string | undefined, prefix?: string): string {
    if (!id)
      return "";
    if (ClientCache.shouldShowIds())
      return id || "";
    return Translator.translate(id, prefix);
  }

  static translate(textToTranslate: string | undefined, prefix?: string): string {
    if (!textToTranslate)
      return textToTranslate || "";
    if (ClientCache.shouldShowIds())
      return textToTranslate || "";
    if (prefix && prefix.length)
      return Translator.translateWithFallback(textToTranslate, prefix + "." + textToTranslate);
    else
      return Translator.translateWithFallback(textToTranslate, textToTranslate);
  }

  static translateInOtherLang(textToTranslate: string | undefined, prefix?: string): string {
    this.switchLang();
    let result = this.translate(textToTranslate, prefix);
    this.restoreLang();
    return result
  }

  static translateWithFallback(fallback: string, textToTranslate: string): string {
    if(!this.dictionary)
      this.calcDictionary();
    if (this.dictionary[textToTranslate])
      return this.dictionary[textToTranslate];
    if (textToTranslate.includes(".")) {
      textToTranslate = textToTranslate.substring(textToTranslate.indexOf(".") + 1, textToTranslate.length);
      return Translator.translateWithFallback(fallback, textToTranslate);
    } else {
      return fallback;
    }
  }

  static calcTranslatedFinalId(textToTranslate?: string, prefix?: string) {
    if(!textToTranslate)
      return textToTranslate;
    if (prefix)
      return this.calcTranslatedFinalId(prefix + "." + textToTranslate);
    if (!this.dictionary)
      this.calcDictionary();
    if (this.dictionary[textToTranslate])
      return textToTranslate;
    if (textToTranslate.includes(".")) {
      textToTranslate = textToTranslate.substring(textToTranslate.indexOf(".") + 1, textToTranslate.length);
      return Translator.calcTranslatedFinalId(textToTranslate);
    } else {
      return textToTranslate;
    }
  }

  static isArabic() {
    return this.locale !== "en";
  }

  static isLanguageRTL() {
    return this.locale !== "en";
  }

  static fetchDir(): string {
    return this.isLanguageRTL() ? 'rtl' : 'ltr';
  }

  static getName(arabic: string | undefined, english: string | undefined): string {
    let name: string | undefined = this.isArabic() ? arabic || english : english || arabic;
    return name ?? "";
  }

  static calcDictionary() {
    if (this.locale === 'en')
      this.dictionary = mWindow.enDictoinary || {};
    else
      this.dictionary = mWindow.arDictoinary || {};
  }

  static initLocale(): void {
    let query = LinkUtils.getQueryParams(document.location.search);
    if (query["locale"]) {
      this.locale = query["locale"].toLowerCase();
      this.localeFromUrl = this.locale;
    } else if (localStorage.getItem("lastPreferredLocale")) {
      this.locale = localStorage.getItem("lastPreferredLocale") === 'en' ? "en" : "ar";
    }
    if (!mWindow.arDictoinary || !mWindow.enDictoinary) {
      mWindow.noNamaServer = true;
    }
    this.calcDictionary();
  }

  static translateNaMaText(text?: namaTypes.NaMaText, prefix?: string): string {
    if (!text)
      return "";
    if (text.arabicText || text.englishText)
      return this.getName(text.arabicText ?? '', text.englishText ?? '');
    return this.translateId(text.resourceId!, prefix);
  }

  static calcByLanguage(arabicText?: string | undefined, englishText?: string | undefined) {
    if (ObjectChecker.isEmptyOrNull(arabicText) && ObjectChecker.isEmptyOrNull(englishText))
      return '';
    if (this.isArabic())
      return ObjectChecker.getFirstNotEmptyObj(arabicText, englishText);
    return ObjectChecker.getFirstNotEmptyObj(englishText, arabicText);
  }

  static translateMenuGroup(group: namaTypes.DTOMenuGroup) {
    return this.translateMenu(group.titleId, group.name1, group.name2, group.code);
  }

  static translateMenu(titleId: string | undefined, name1: string | undefined, name2: string | undefined, code: string | undefined) {
    if (ClientCache.shouldShowIds() && titleId)
      return titleId;
    let translation: string | undefined = this.getName(name1, name2);
    if (!translation)
      translation = this.translateId(titleId);
    if (!translation)
      translation = code;
    return translation;
  }

  static translateMenuItem(item: namaTypes.DTOMenuItem): string | undefined {
    return this.translateMenu(item.titleId, item.name1, item.name2, undefined);
  }

  static restoreLang() {
    if (this.originalLang == null)
      return;
    this.locale = this.originalLang;
    this.calcDictionary();
  }

  static translateRef(ref: namaTypes.EntityReferenceData | undefined) {
    if (!ref || !ref.id)
      return "";
    let code = ref[CommonFieldIds.CODE];
    let altCode = ref[CommonFieldIds.ALT_CODE];
    let name1 = ref[CommonFieldIds.NAME1];
    let name2 = ref[CommonFieldIds.NAME2];
    let id = ref[CommonFieldIds.ID];
    let descriptor1 = "";
    let descriptor2 = "";
    if (!ClientCache.getCustomGUISettings()?.doNotUseDescriptorsInFields) {
      descriptor1 = ref[CommonFieldIds.DESCRIPTOR1] ?? "";
      descriptor2 = ref[CommonFieldIds.DESCRIPTOR2] ?? "";
    }
    return ObjectChecker.getFirstNotEmptyObj(
      this.getName(ObjectChecker.getFirstNotEmptyObj(descriptor1, name1), ObjectChecker.getFirstNotEmptyObj(descriptor2, name2)),
      this.getName(code, altCode), id);
  }

  static getListViewTitle(entityType: string, viewName?: string | undefined, layout?: namaTypes.ListScreenLayout | null | undefined, menuItem?: namaTypes.DTOMenuItem | null): string {
    if (menuItem && menuItem.target?.targetEntity === entityType && (menuItem.listViewArTitle || menuItem.listViewEnTitle))
      return this.getName(menuItem.listViewArTitle, menuItem.listViewEnTitle);
    if (layout) {
      if (layout.title)
        return this.translateTitledId(entityType, layout);
      let translatedLayoutId = this.translateLayoutId(layout.id);
      if (translatedLayoutId)
        return translatedLayoutId;
    }
    let translatedLayoutId = this.translateLayoutId(viewName);
    if (translatedLayoutId)
      return translatedLayoutId;
    return this.translateId(entityType + "s");
  }

  static translateLayoutId(layoutId: string | undefined) {
    if (ClientCache.shouldShowIds())
      return layoutId || '';
    if (layoutId && layoutId !== StringConstants.DEFAULT)
      return this.translateId(layoutId);
    return '';
  }

  static switchLang() {
    if (this.originalLang === null)
      this.originalLang = this.locale;
    if (this.originalLang === 'en')
      this.locale = 'ar';
    else
      this.locale = 'en';
    this.calcDictionary();
  }

  static translateTitledId(ownerId: string | undefined, titledId?: namaTypes.TitledID): string {
    if (!titledId)
      return "";
    if (ClientCache.shouldShowIds())
      return titledId.id ?? '';
    if (titledId && titledId.title && (titledId.title.resourceId || titledId.title.arabicText || titledId.title.englishText))
      return this.translateNaMaText(titledId.title);
    let titleToTranslate: any = titledId;
    if (titledId.id)
      titleToTranslate = titledId.id;
    if (typeof titleToTranslate == "object")
      return "";
    return this.translate(titleToTranslate, ownerId);
  }

  static translateTitledIdInOtherLang(ownerId: string | undefined, titledId: namaTypes.TitledID) {
    this.switchLang()
    let title = this.translateTitledId(ownerId, titledId);
    this.restoreLang()
    return title;
  }

  static translateIdInOtherLang(id: string | undefined, prefix?: string | undefined) {
    this.switchLang();
    let text = this.translateId(id, prefix);
    this.restoreLang();
    return text;
  }

  static translateTitledIDWithPrefixes(object: Record<string, any> | null | undefined, idPrefix: string | null | undefined) {
    if (!object)
      return "";
    if (object.title)
      return this.translateNaMaText(object.title);
    let id = object.id;
    if (!id)
      return "";
    if (idPrefix)
      id = idPrefix + "." + id;
    let lastPart = this.translateId(id);
    let prefixes: string[] = [];
    do {
      id = StringUtils.substringBeforeLast(id, ".");
      if (ObjectChecker.areEqual(idPrefix, id) || !id || id.indexOf('.') < 0)
        break;
      let str = this.translateId(id);
      if (str && str !== id && !prefixes.contains(str))
        prefixes.unshift(str);
    }
    while (true);
    let translation = lastPart;
    if (ObjectChecker.isNotEmptyOrNull(prefixes))
      translation = prefixes.join(" - ") + " - " + translation;
    return translation;
  }

  static translateNaMaTextEn(text: namaTypes.NaMaText) {
    if (this.isArabic())
      this.switchLang();
    let txt = this.translateNaMaText(text);
    this.restoreLang();
    return txt;
  }

  static translateNaMaTextAr(text: namaTypes.NaMaText) {
    if (this.isEnglish())
      this.switchLang();
    let txt = this.translateNaMaText(text);
    this.restoreLang();
    return txt;
  }

  static isEnglish() {
    return !this.isArabic();
  }

  static fetchAGGridLocale() {
    this.applyCustomAGLocaleOverrides();
    if (this.isArabic())
      return AG_GRID_LOCALE_EG;
    return AG_GRID_LOCALE_EN;
  }

  private static applyCustomAGLocaleOverrides() {
    AG_GRID_LOCALE_EG.totalRows = "عدد الصفوف"
    AG_GRID_LOCALE_EN.totalRows = "Rows Count"
  }

  static getDetailsViewTitlePrefix(entityType: string | undefined, line: namaTypes.DTOMenuItem | null): string {
    if (line && ObjectChecker.areEqual(line.target?.targetEntity, entityType) && ObjectChecker.getFirstNotEmptyObj(line.editViewArTitle, line.editViewEnTitle)) {
      return Translator.getName(line.editViewArTitle, line.editViewEnTitle);
    }
    return Translator.translateId(entityType);
  }

  static getNameForFields(object: namaTypes.BaseEntityDTO) {
    return Translator.getNameOfAnEntity(object, true);
  }

  private static getNameOfAnEntity(object: namaTypes.BaseEntityDTO, forFieldDisplay: boolean) {
    if (!ClientCache.getCustomGUISettings()?.doNotUseDescriptorsInFields || !forFieldDisplay)
      return this.getNameConsideringDescriptors(object);
    return ObjectChecker.getFirstNotNullObj(this.getName(object[CommonFieldIds.NAME1], object[CommonFieldIds.NAME2]), this.getCode(object), object.id);
  }

  static getNameForTitle(object: namaTypes.BaseEntityDTO) {
    return Translator.getNameOfAnEntity(object, false);
  }

  static getNameConsideringDescriptors(object: namaTypes.BaseEntityDTO) {
    return ObjectChecker.getFirstNotEmptyObj(this.getName(object[CommonFieldIds.DESCRIPTOR1], object[CommonFieldIds.DESCRIPTOR2]),
      this.getName(object[CommonFieldIds.NAME1], object[CommonFieldIds.NAME2]), this.getCode(object), object.id);
  }

  static getCode(object: namaTypes.BaseEntityDTO): string {
    return this.getName(object.code, object[CommonFieldIds.ALT_CODE])
  }

  static translateIdAr(id: string | undefined, prefix?: string) {
    if (this.isEnglish())
      this.switchLang();
    let txt = this.translateId(id, prefix);
    this.restoreLang();
    return txt;
  }

  static translateIdEn(id: string | undefined, prefix?: string) {
    if (this.isArabic())
      this.switchLang();
    let txt = this.translateId(id, prefix);
    this.restoreLang();
    return txt;
  }

  static currentLang() {
    return this.isArabic() ? Language.Arabic : Language.English;
  }

  static translateMessage(msg: string | undefined, ...args: string[]): string {
    return this.formatMessage(this.translateId(msg), ...args);
  }

  static formatMessage(msg: string, ...args: string[]): string {
    for (let i = 0; i < args.length; i++) {
      let arg = args[i];
      if (ObjectChecker.isNullOrUndefined(arg))
        arg = "";
      arg = arg + "";
      msg = msg.split("{" + i + "}").join(arg);
    }
    return msg;
  }

  static translateAGColumn(gridApi: GridApi, colId: string) {
    const columnDef = gridApi.getColumnDef(colId);
    return this.translateId(columnDef?.context.fieldId ?? colId)
  }

  static toNaMaText(resourceId: string): NaMaText {
    return {
      arabicText: Translator.translateIdAr(resourceId),
      englishText: Translator.translateIdEn(resourceId),
      resourceId
    }
  }
}

export interface ElementTranslationInfo extends NaMaText {
  finalId?: string,
  forType?: string
  id?: string,
  lineId?: string,
  currentArabic?: string,
  currentEnglish?: string
}
